import * as React from 'react';
import styled from 'styled-components';
import { InputFieldStyling, InputWrapper, RequiredStar } from './styling';

type PasswordFieldProps = {
  id: string;
  disabled?: boolean;
  onChange?: (text: string) => void;
  hasError?: boolean;
  value?: string;
  validationMessage?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
};

const StyledPassSwitch = styled.div`
  float: right;
  margin-left: -40px;
  margin-top: 12px;
  position: relative;
  z-index: 2;
  height: 30px;
  width: 30px;

  svg {
    cursor: pointer;
  }
  div {
    &:focus {
      outline: none;
      svg {
        box-shadow: 0px 4px 12px rgba(46, 127, 161, 0.25), 0px 0px 8px #2e7fa1;
        border-radius: 4px;
      }
    }
  }
`;

const PasswordRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const PasswordField = ({ id, disabled, onChange, hasError, value, validationMessage, autoComplete, placeholder, required }: PasswordFieldProps) => {
  const [passwordHidden, setPasswordHidden] = React.useState<Boolean>(true);
  const inputRef = React.useRef<any>(null);
  const [tabbedHere, setTabbedHere] = React.useState<boolean>(false);

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      setPasswordHidden(!passwordHidden);
    }
  };
  return (
    <>
      {required && <RequiredStar>*</RequiredStar>}
      <InputWrapper
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onFocus={(e: any) => {
          if (!e?.target?.id.includes(id) && !disabled) {
            setTabbedHere(true);
            inputRef.current.focus();
          }
        }}>
        <div style={{ display: 'block' }}>
          <PasswordRow>
            <InputFieldStyling
              id={id}
              ref={inputRef}
              type={passwordHidden ? 'password' : 'text'}
              name="password"
              value={value}
              className={hasError ? 'error' : ''}
              tabIndex={-1}
              autoComplete={autoComplete}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
              tabbedHere={tabbedHere}
              onBlur={() => setTabbedHere(false)}
            />
            <StyledPassSwitch>
              {!disabled ? (
                passwordHidden ? (
                  <div id={`${id}_Visible`} tabIndex={0} onKeyDown={handleKeyDown} onClick={() => setPasswordHidden(!passwordHidden)}>
                    <svg viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M4.05966 12.5853L3.25603 11.7821C2.86542 11.3917 2.23234 11.3918 1.84186 11.7823C1.45115 12.173 1.45115 12.8065 1.84186 13.1972L2.6448 14.0001C2.8984 14.2537 3.1593 14.4956 3.42683 14.7256L1.84172 16.3107C1.4512 16.7013 1.4512 17.3344 1.84172 17.725C2.23225 18.1155 2.86541 18.1155 3.25593 17.725L5.0508 15.9301C5.73486 16.3644 6.44837 16.7336 7.18249 17.0377L6.28268 19.5236C6.09471 20.0429 6.36331 20.6163 6.88262 20.8043C7.40193 20.9922 7.9753 20.7236 8.16327 20.2043L9.08108 17.6687C9.89493 17.8758 10.7238 18.0088 11.5569 18.0675C11.5516 18.1092 11.5488 18.1517 11.5488 18.1948V20.4897C11.5488 21.042 11.9965 21.4897 12.5488 21.4897C13.1011 21.4897 13.5488 21.042 13.5488 20.4897V18.1948C13.5488 18.1517 13.5461 18.1092 13.5408 18.0675C14.3739 18.0088 15.2027 17.8758 16.0166 17.6687L16.9344 20.2043C17.1224 20.7236 17.6957 20.9922 18.215 20.8043C18.7343 20.6163 19.003 20.0429 18.815 19.5236L17.9152 17.0377C18.6462 16.7349 19.3568 16.3675 20.0382 15.9356L21.8276 17.725C22.2181 18.1155 22.8513 18.1155 23.2418 17.725C23.6323 17.3344 23.6323 16.7013 23.2418 16.3107L21.6632 14.7322C21.9334 14.5001 22.1969 14.2561 22.4529 14.0001L23.2558 13.1972C23.6465 12.8065 23.6465 12.173 23.2558 11.7823C22.8653 11.3918 22.2322 11.3917 21.8416 11.7821L21.038 12.5853C16.3496 17.2737 8.7481 17.2737 4.05966 12.5853Z"
                        fill="#666666"></path>
                    </svg>
                  </div>
                ) : (
                  <div id={`${id}_NotVisible`} tabIndex={0} onKeyDown={handleKeyDown} onClick={() => setPasswordHidden(!passwordHidden)}>
                    <svg viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12.9618 15.4897C14.6186 15.4897 15.9618 14.1466 15.9618 12.4897C15.9618 10.8329 14.6186 9.48975 12.9618 9.48975C11.3049 9.48975 9.96179 10.8329 9.96179 12.4897C9.96179 14.1466 11.3049 15.4897 12.9618 15.4897Z"
                        fill="#666666"
                      />
                      <path
                        d="M2.87753 10.83L2.14123 11.9081C1.90198 12.2585 1.90198 12.721 2.14123 13.0714L2.87753 14.1495C7.74011 21.2698 18.1835 21.2698 23.0461 14.1495L23.7824 13.0714C24.0216 12.721 24.0216 12.2585 23.7824 11.9081L23.0461 10.83C18.1835 3.70968 7.74011 3.70967 2.87753 10.83ZM21.3944 11.9579L21.7577 12.4897L21.3944 13.0216C17.326 18.9791 8.59763 18.9791 4.52914 13.0216L4.16592 12.4897L4.52914 11.9579C8.59763 6.00037 17.326 6.00037 21.3944 11.9579Z"
                        fill="#666666"
                      />
                    </svg>
                  </div>
                )
              ) : null}
            </StyledPassSwitch>
          </PasswordRow>
        </div>
      </InputWrapper>
      {validationMessage && <span className="error-msg">{validationMessage}</span>}
    </>
  );
};

export default PasswordField;
